/**
 * Token inspector — the player/DM's focused editing surface for one token
 * (phase1-plan §4: "your stats and active effects readable at a glance … one
 * click to adjust HP (±1/±5/±10, matching Battle Mode's idiom)").
 *
 * This is the INTERACTIVE panel. It is NOT the machine-readable mirror:
 * the §6.2 `data-testid="stat"` / `effect-chip` elements live in
 * `tokenMirror.ts` so their values have exactly one source. The inspector's
 * controls carry `data-testid="stat-control"` and aria-labels; E2E clicks
 * these and reads the result from the mirror.
 *
 * Plain DOM, no framework (§2), unit-tested under jsdom. It only reads a
 * Token and emits intents through a callback — it never touches net/ or the
 * shared validator directly, so the net layer can change underneath it.
 */

import type { Effect, StatKey, Token } from "@asarimitsuha/table-core";

/** Battle-Mode bar idiom: fixed order, label, and accent colour per stat.
 *  Colours are approximate to Battle Mode and are not asserted by tests
 *  (E2E is DOM-not-pixels); tune freely without touching logic. */
interface StatMeta {
  key: StatKey;
  label: string;
  color: string;
}

export const STAT_META: readonly StatMeta[] = [
  { key: "hp", label: "HP", color: "#c0392b" },
  { key: "shield", label: "Shield", color: "#5dade2" },
  { key: "mana", label: "Mana", color: "#8e44ad" },
  { key: "rp", label: "RP", color: "#e0a423" },
];

/** The ± steps, mirroring Battle Mode (§4). Rendered −10 −5 −1 +1 +5 +10. */
export const STAT_STEPS: readonly number[] = [-10, -5, -1, 1, 5, 10];

export interface TokenInspectorCallbacks {
  /** Emit an adjust-stat intent. Wiring maps this to the shared adjustStat
   *  command; the reducer clamps and reports the applied delta (CMD-10), so
   *  the inspector never does bounds math itself. */
  onAdjustStat(tokenId: string, stat: StatKey, delta: number): void;
  /** Selection changed via the inspector's own selector (null = none).
   *  Optional so read-only mounts need no wiring. */
  onSelectToken?(tokenId: string | null): void;
  /** Remove an effect from this token (decision 9's three-party rule —
   *  the caller decides who sees the button via canRemoveEffect). */
  onRemoveEffect?(effectId: string): void;
}

export interface TokenInspectorOptions {
  /** Whether the viewer may edit this token (GM, or the token's owner). When
   *  false the ± controls are disabled — honest affordance, but the server
   *  is still the authority (CMD-05); disabling is UX, not security. */
  canEdit: boolean;
  /** Effects currently on this token, for the "active effects" area. Empty
   *  in M2 (no applyEffect command yet); the area is ready for M3's chips. */
  effects: readonly Effect[];
  /** All room tokens, for the keyboard-accessible selector. When provided,
   *  the inspector renders a native <select> so selection does not require
   *  clicking the canvas — the canvas is invisible to keyboard and
   *  screen-reader users, and the mirror is read-only by design. */
  tokens?: Record<string, Token>;
  /** Whether the viewer may remove a given effect (GM / applier via source
   *  token / target's owner — decision 9). Only shown-when-true: an
   *  affordance the server would refuse is a lie (§6.6.2 spirit). */
  canRemoveEffect?: (effect: Effect) => boolean;
}

function formatStep(delta: number): string {
  // Unicode minus for negatives; explicit plus for positives.
  return delta < 0 ? `−${Math.abs(delta)}` : `+${delta}`;
}

function fillPercent(current: number, max: number): number {
  if (max <= 0) return 0;
  const ratio = current / max;
  if (ratio <= 0) return 0;
  if (ratio >= 1) return 100;
  return ratio * 100;
}

function renderStatRow(
  token: Token,
  meta: StatMeta,
  options: TokenInspectorOptions,
  cb: TokenInspectorCallbacks,
): HTMLElement {
  const value = token.stats[meta.key];
  const row = document.createElement("div");
  row.className = "stat-row";
  row.dataset["statRow"] = meta.key;

  const label = document.createElement("span");
  label.className = "stat-label";
  label.textContent = meta.label;

  // Accessible meter: screen readers announce current/max; the visual fill is
  // decorative (aria-hidden via the meter carrying the semantics).
  const bar = document.createElement("div");
  bar.className = "stat-bar";
  bar.setAttribute("role", "meter");
  bar.setAttribute("aria-label", `${token.name} ${meta.label}`);
  bar.setAttribute("aria-valuemin", "0");
  bar.setAttribute("aria-valuemax", String(value.max));
  bar.setAttribute("aria-valuenow", String(value.current));
  bar.setAttribute("aria-valuetext", `${value.current} of ${value.max}`);
  const fill = document.createElement("div");
  fill.className = "stat-fill";
  fill.style.width = `${fillPercent(value.current, value.max)}%`;
  fill.style.background = meta.color;
  bar.appendChild(fill);

  const readout = document.createElement("span");
  readout.className = "stat-readout";
  readout.textContent = `${value.current} / ${value.max}`;

  const controls = document.createElement("div");
  controls.className = "stat-controls";
  controls.setAttribute("role", "group");
  controls.setAttribute("aria-label", `Adjust ${token.name} ${meta.label}`);
  for (const delta of STAT_STEPS) {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset["testid"] = "stat-control";
    button.dataset["tokenId"] = token.id;
    button.dataset["stat"] = meta.key;
    button.dataset["delta"] = String(delta);
    button.textContent = formatStep(delta);
    button.setAttribute("aria-label", `${token.name} ${meta.label} ${formatStep(delta)}`);
    button.disabled = !options.canEdit;
    button.addEventListener("click", () => {
      cb.onAdjustStat(token.id, meta.key, delta);
    });
    controls.appendChild(button);
  }

  row.append(label, bar, readout, controls);
  return row;
}

function renderEffectsArea(
  options: TokenInspectorOptions,
  cb: TokenInspectorCallbacks,
): HTMLElement {
  const section = document.createElement("section");
  section.dataset["testid"] = "token-effects";
  section.setAttribute("aria-label", "Active effects");

  const heading = document.createElement("h3");
  heading.textContent = "Effects";
  section.appendChild(heading);

  if (options.effects.length === 0) {
    const empty = document.createElement("p");
    empty.className = "effects-empty";
    empty.textContent = "No active effects.";
    section.appendChild(empty);
    return section;
  }

  // Presentational list only. The canonical machine-readable effect chips
  // (data-testid="effect-chip") live in the mirror; here we render a plain,
  // screen-reader-friendly summary so the two never duplicate that testid.
  const list = document.createElement("ul");
  list.className = "effects-list";
  for (const effect of options.effects) {
    const item = document.createElement("li");
    item.className = "effect-item";
    item.dataset["effectId"] = effect.id;
    const remaining =
      effect.expiry === "untilRemoved"
        ? "until removed"
        : `${effect.expiry.roundsRemaining} more`;
    const text = document.createElement("span");
    text.textContent = `${effect.name} · ${effect.kind} · ${remaining}`;
    item.appendChild(text);

    if (options.canRemoveEffect?.(effect) === true) {
      const remove = document.createElement("button");
      remove.type = "button";
      remove.dataset["testid"] = "remove-effect";
      remove.dataset["effectId"] = effect.id;
      remove.textContent = "Remove";
      remove.setAttribute("aria-label", `Remove ${effect.name}`);
      remove.addEventListener("click", () => cb.onRemoveEffect?.(effect.id));
      item.appendChild(remove);
    }
    list.appendChild(item);
  }
  section.appendChild(list);
  return section;
}

/** The keyboard/screen-reader path to selection: a native, labelled
 *  <select> over all room tokens. Canvas click stays the pointer path; this
 *  is the one that works without a pointer. */
function renderTokenSelect(
  selected: Token | null,
  tokens: Record<string, Token>,
  cb: TokenInspectorCallbacks,
): HTMLElement {
  const label = document.createElement("label");
  label.className = "inspector-select";
  label.textContent = "Inspect token";

  const select = document.createElement("select");
  select.dataset["testid"] = "token-select";

  const none = document.createElement("option");
  none.value = "";
  none.textContent = "— none —";
  select.appendChild(none);

  const sorted = Object.values(tokens).sort(
    (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
  );
  for (const token of sorted) {
    const option = document.createElement("option");
    option.value = token.id;
    option.textContent = token.name;
    select.appendChild(option);
  }
  select.value = selected !== null && tokens[selected.id] ? selected.id : "";
  select.addEventListener("change", () => {
    cb.onSelectToken?.(select.value === "" ? null : select.value);
  });

  label.appendChild(select);
  return label;
}

/**
 * Render the inspector for `token` into `root`. A null token renders the
 * empty state (nothing selected). Idempotent: re-rendering replaces children.
 */
export function renderTokenInspector(
  root: HTMLElement,
  token: Token | null,
  options: TokenInspectorOptions,
  cb: TokenInspectorCallbacks,
): void {
  root.replaceChildren();
  root.dataset["testid"] = "token-inspector";

  if (options.tokens !== undefined) {
    root.appendChild(renderTokenSelect(token, options.tokens, cb));
  }

  if (token === null) {
    root.dataset["tokenId"] = "";
    const empty = document.createElement("p");
    empty.className = "inspector-empty";
    empty.textContent = "Select a token to inspect it.";
    root.appendChild(empty);
    return;
  }

  root.dataset["tokenId"] = token.id;

  const header = document.createElement("header");
  header.className = "inspector-header";

  if (token.portrait !== null) {
    const portrait = document.createElement("img");
    portrait.className = "inspector-portrait";
    portrait.src = token.portrait;
    // Decorative-with-name: the heading carries the name, so alt is the name
    // to avoid a screen reader announcing an unlabeled image.
    portrait.alt = token.name;
    header.appendChild(portrait);
  }

  const name = document.createElement("h2");
  name.className = "inspector-name";
  name.textContent = token.name;
  header.appendChild(name);
  root.appendChild(header);

  const stats = document.createElement("div");
  stats.className = "inspector-stats";
  for (const meta of STAT_META) {
    stats.appendChild(renderStatRow(token, meta, options, cb));
  }
  root.appendChild(stats);

  root.appendChild(renderEffectsArea(options, cb));
}
